import {
  ApiError,
  BookingListingVariant,
  GetListingResponse,
  OfferListingVariant,
  PostListingEnquiryMessageBody,
  ProductListingVariant,
  SubscriptionListingVariant,
} from "@givenwell/marketplace-api";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { notFound } from "@tanstack/react-router";
import { api } from "./utils";

export const listingCategoriesQuery = queryOptions({
  queryKey: ["listing-categories"],
  queryFn: async () => await api.listings.getListingCategories(),
});

export const eapCategoriesQuery = queryOptions({
  queryKey: ["categories", "eap"],
  queryFn: () => api.categories.listCategories({ categoryType: "eap" }),
});

export const listingQuery = (listingId: string) => ({
  queryKey: ["listings", listingId],
  queryFn: async () => {
    try {
      return await api.listings.getListing({ listingId });
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        throw notFound();
      }
      throw e;
    }
  },
});

export const listingTagsQuery = queryOptions({
  queryKey: ["listing-tags"],
  queryFn: async () => {
    return await api.listings.getTagsForMembers();
  },
});

export const TagSet = {
  Specialty: "speciality",
  SupportType: "aos",
  Gender: "gender",
  Language: "language",
} as const;
export type TagSet = (typeof TagSet)[keyof typeof TagSet];

export const tagsQuery = (tagSet: TagSet) =>
  queryOptions({
    queryKey: ["tags", tagSet],
    queryFn: async () => {
      return await api.listings.getTagSetForMembers({ tagSet });
    },
  });

export type Listing = GetListingResponse;
export type Variant = GetListingResponse["variants"][number];

export function isBookingVariant(variant: Variant): variant is BookingListingVariant {
  return variant.type === "booking";
}
export function isProductVariant(variant: Variant): variant is ProductListingVariant {
  return variant.type === "product";
}
export function isSubscriptionVariant(variant: Variant): variant is SubscriptionListingVariant {
  return variant.type === "subscription";
}
export function isOfferVariant(variant: Variant): variant is OfferListingVariant {
  return variant.type === "offer";
}

type ListingAvailabilityQueryParams = {
  month: { year: number; month: number };
  listingId: string;
  variantId: string;
  durationMinutes: number;
  bufferMinutes: number;
};

export const listingAvailabilityQueryKey = ["listing-availability"];

export const listingVariantAvailabilityQuery = (params: ListingAvailabilityQueryParams) => {
  const timezoneIana = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const monthStart = new Date(params.month.year, params.month.month - 1, 1);
  const monthEnd = new Date(params.month.year, params.month.month, 1);

  return queryOptions({
    queryKey: [...listingAvailabilityQueryKey, params, timezoneIana],
    queryFn: async () => {
      return await api.listings.getListingVariantAvailability({
        listingId: params.listingId,
        variantId: params.variantId,
        tz: timezoneIana,
        from: monthStart.toISOString(),
        to: monthEnd.toISOString(),
        duration: params.durationMinutes,
        buffer: params.bufferMinutes,
      });
    },
  });
};

export function usePostListingEnquiryMessageMutation(listingId: string) {
  return useMutation({
    mutationFn: async (requestBody?: PostListingEnquiryMessageBody) => {
      return await api.listings.postListingEnquiryMessage({ listingId, requestBody });
    },
  });
}
